import { useState } from 'react';
import { Drawer, Empty, Input, Pagination, Select, Spin, Tag } from 'antd';
import dayjs from 'dayjs';
import { useActivityLogs } from '@presentation/hooks/useActivityLogs';

interface ActivityLogDrawerProps {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
}

const ACTION_GROUPS = [
  { label: 'All actions', value: '' },
  { label: 'Collections', value: 'collection' },
  { label: 'Folders', value: 'folder' },
  { label: 'Requests', value: 'request' },
  { label: 'Environments', value: 'environment' },
  { label: 'Cookies', value: 'cookie' },
  { label: 'Mock servers', value: 'mock_server' },
  { label: 'Workspace', value: 'workspace' },
];

const actionColor = (action: string): string => {
  if (action.endsWith('.created') || action.endsWith('.imported')) return 'green';
  if (action.endsWith('.deleted')) return 'red';
  if (action.endsWith('.updated')) return 'blue';
  return 'default';
};

export const ActivityLogDrawer = ({ workspaceId, open, onClose }: ActivityLogDrawerProps) => {
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useActivityLogs(workspaceId, {
    search: search || undefined,
    action: action || undefined,
    page,
  });

  return (
    <Drawer title="Activity Log" open={open} onClose={onClose} size={520}>
      <div className="mb-3 flex gap-2">
        <Input.Search
          placeholder="Search activity"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          allowClear
          className="flex-1"
        />
        <Select
          value={action}
          onChange={(value) => {
            setAction(value);
            setPage(1);
          }}
          options={ACTION_GROUPS}
          className="w-40"
        />
      </div>

      {isLoading && (
        <div className="flex justify-center py-6">
          <Spin size="small" />
        </div>
      )}

      {!isLoading && data?.data.length === 0 && <Empty description="No activity yet" />}

      <div className="flex flex-col divide-y divide-black/5 dark:divide-white/10">
        {data?.data.map((entry) => (
          <div key={entry.id} className="flex flex-col gap-1 py-2.5">
            <div className="flex items-center gap-2">
              <Tag color={actionColor(entry.action)} className="shrink-0 font-mono text-[11px]">
                {entry.action}
              </Tag>
              <span className="text-xs text-gray-400">
                {dayjs(entry.createdAt).format('YYYY-MM-DD HH:mm')}
              </span>
            </div>
            <span className="text-sm">{entry.description}</span>
            {entry.user && <span className="text-xs text-gray-400">by {entry.user.name}</span>}
          </div>
        ))}
      </div>

      {data && data.meta.total > data.meta.perPage && (
        <div className="mt-3 flex justify-center">
          <Pagination
            size="small"
            current={data.meta.currentPage}
            pageSize={data.meta.perPage}
            total={data.meta.total}
            onChange={setPage}
            showSizeChanger={false}
          />
        </div>
      )}
    </Drawer>
  );
};
